import { Icon } from "./Icons";
import { DismissAdd } from "./DismissAdd";
import { daysBetween, shortDay } from "../lib/offersPage";
import type { AddedOffer } from "../lib/offerAdds";
import type { RecommendedCard } from "../lib/worthAdding";

const LABEL = "px-3 pt-2.5 pb-0.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-500";

/**
 * Added: what runs added for you, across every issuer, pinned ones on top.
 * Dismiss all leaves pinned rows alone, and a pinned row has no ×: it stays
 * until it is unpinned or ends.
 */
export function AddedForYou({ added, today, soonDays, isPinned, isBusy = () => false, onPin, onDismiss }: {
  added: AddedOffer[];
  today: string;
  soonDays: number;
  isPinned: (offer: AddedOffer) => boolean;
  isBusy?: (offer: AddedOffer) => boolean;
  onPin: (offer: AddedOffer, pinned: boolean) => void;
  onDismiss: (offers: AddedOffer[]) => void;
}) {
  if (added.length === 0) {
    return (
      <div className="rounded-xl border border-neutral-800 bg-neutral-900 px-3 py-3 text-[13px] text-neutral-400">
        Nothing added since you last cleared this.
      </div>
    );
  }
  const pinned = added.filter(isPinned);
  const rest = added.filter((a) => !isPinned(a));
  const row = (a: AddedOffer) => (
    <AddedLine
      key={a.key}
      added={a}
      today={today}
      soonDays={soonDays}
      pinned={isPinned(a)}
      busy={isBusy(a)}
      onPin={onPin}
      onDismiss={() => { onDismiss([a]); }}
    />
  );
  return (
    <div className="overflow-hidden rounded-xl border border-neutral-800 bg-neutral-900">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-neutral-800 px-3 py-2">
        <span className="text-[14px] font-semibold">Added for you</span>
        <span className="text-[11.5px] text-neutral-500">From the last runs</span>
        {rest.length > 0 && (
          <button type="button" onClick={() => { onDismiss(rest); }} className="btn btn-ghost btn-sm ml-auto">
            Dismiss all unpinned
          </button>
        )}
      </div>
      {pinned.length > 0 && (
        <section aria-label="Pinned added offers">
          <p className={LABEL}>Pinned</p>
          <ul className="divide-y divide-neutral-800">{pinned.map(row)}</ul>
        </section>
      )}
      {rest.length > 0 && (
        <section aria-label="Added offers">
          {pinned.length > 0 && <p className={LABEL}>Added</p>}
          <ul className="divide-y divide-neutral-800">{rest.map(row)}</ul>
        </section>
      )}
    </div>
  );
}

function AddedLine({ added: a, today, soonDays, pinned, busy, onPin, onDismiss }: {
  added: AddedOffer;
  today: string;
  soonDays: number;
  pinned: boolean;
  busy: boolean;
  onPin: (offer: AddedOffer, pinned: boolean) => void;
  onDismiss: () => void;
}) {
  const left = daysBetween(today, a.expires_on);
  const soon = left !== null && left >= 0 && left <= soonDays;
  const canPin = (a.offer_ids ?? []).length > 0;
  return (
    <li className="flex items-start gap-2 px-3 py-2.5 text-[12.5px]">
      <Icon name="check" size={15} strokeWidth={2.2} className="mt-0.5 shrink-0 text-green-400" />
      <div className="min-w-0 flex-1">
        <span className="font-semibold">{a.merchant}</span>
        {a.terms && <span className="text-neutral-300"> · {a.terms}</span>}
        <div className="text-[11.5px] text-neutral-500">
          {a.issuer && <span>{a.issuer}: </span>}
          {a.cards.map((c, i) => (
            <span key={c.card_name}>
              {i > 0 && ", "}
              {c.card_name}
              {c.state === "vanished"
                ? <span className="text-amber-400"> left the list</span>
                : c.state !== "confirmed" && <span className="text-amber-400"> not confirmed</span>}
            </span>
          ))}
          {a.expires_on && (
            <span className={soon ? "text-amber-400" : ""}> · ends {shortDay(a.expires_on)}</span>
          )}
        </div>
        {pinned && (
          <div className="text-[11.5px] text-neutral-500">Pinned: the podcast reminds you before it ends</div>
        )}
      </div>
      {canPin && (
        <button
          type="button"
          disabled={busy}
          aria-pressed={pinned}
          aria-label={`${pinned ? "Unpin" : "Pin"} ${a.merchant}`}
          onClick={() => { onPin(a, !pinned); }}
          className={`btn btn-sm shrink-0 ${pinned ? "btn-primary" : "btn-ghost"}`}
        >
          {pinned ? "Pinned" : "Pin"}
        </button>
      )}
      {!pinned && <DismissAdd merchant={a.merchant} onClick={onDismiss} />}
    </li>
  );
}

/** A press that left the card's list: nobody can say whether it took. */
export function VanishedAdd({ added: a, cards, onDismiss }: {
  added: AddedOffer;
  cards: RecommendedCard[];
  onDismiss: () => void;
}) {
  const gone = a.cards.filter((c) => c.state === "vanished");
  const url = cards.find((card) => gone.some((c) => c.card_name === card.card_name))?.url ?? cards[0]?.url;
  return (
    <li className="flex flex-wrap items-start gap-x-3 gap-y-1.5 px-3 py-2.5">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[14px] font-semibold">{a.merchant}</span>
        {a.terms && <span className="text-[13px] text-neutral-300">{a.terms}</span>}
        <span className="text-xs text-neutral-500">On {gone.map((c) => c.card_name).join(", ")}</span>
        <span className="text-xs text-amber-400">
          Left the card's list after the press; check the issuer's site
        </span>
      </div>
      <span className="flex items-center gap-1.5">
        {url && (
          <a href={url} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm">
            Open {a.issuer ?? "issuer"}
          </a>
        )}
        <DismissAdd merchant={a.merchant} onClick={onDismiss} />
      </span>
    </li>
  );
}
